import React, { useState, useEffect, useCallback } from 'react';
import { FaHistory, FaEye, FaUndo, FaTimes } from 'react-icons/fa';
import { apiFetch } from '../../utils/apiAdmin';
import { sanitiseWikiHtml } from '../../utils/wikiSanitise';

/**
 * A page's version history, and the rollback that reads from it.
 *
 * Planning/TME-WIKI-2026-09-02.md §4.3, §6.3, §10.3, D3, F24.
 *
 * ⚠️ A VERSION IS A SNAPSHOT OF THREE THINGS, NOT ONE (§4.3). It captures the
 * body, the title AND the tags as they were at that save - and because a
 * metadata-only save writes no version (D3), a version's title may LAG the live
 * title. That is intended, and it is why the confirmation below says in words
 * what a rollback restores: a reader looking at a body diff would not expect
 * the title to move with it.
 *
 * ⚠️ NOTHING IS REWOUND. A rollback is a save like any other: it writes a NEW
 * version recording what was restored, and every later version stays in the
 * list. There is no destructive operation on this panel.
 *
 * ⚠️ THE READ PATH SANITISES (§7.1). A stored version body predates any future
 * allow-list change just as a live body does, so the preview purifies at the
 * moment it renders rather than trusting what was stored.
 */

const API = `${process.env.REACT_APP_API_URL}/admin/tme-wiki`;

/**
 * The words the confirmation uses (F24), exported so the test asserts the
 * SENTENCE rather than a paraphrase of it.
 *
 * It names all three restored things explicitly, because the panel lists
 * versions by number and date - which says nothing about the title and tags
 * travelling with the body.
 */
export function rollbackConfirmation(versionNumber) {
    return `Roll back to version ${versionNumber}?\n\n`
        + 'The body, the title and the tags are all restored exactly as they were '
        + 'captured in that version - so a title or a tag changed since then goes '
        + 'back too.\n\n'
        + 'This is saved as a new version. Nothing is removed from the history.';
}

/** "2026-09-02 14:31" from an ISO timestamp, without pulling in a date library. */
const stamp = iso => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '');

const VersionHistoryPanel = ({ wikiId, page, expanded, onToggle, readOnly = false, onRolledBack, refreshKey = 0 }) => {
    const [versions, setVersions] = useState([]);
    const [error, setError] = useState(null);
    const [viewing, setViewing] = useState(null);
    const [busy, setBusy] = useState(false);

    const pageId = page?.pageId;

    const fetchVersions = useCallback(async ({ silent = false } = {}) => {
        if (!pageId || !expanded) return;
        if (!silent) setError(null);
        try {
            const response = await apiFetch(`${API}/wikis/${wikiId}/pages/${pageId}/versions`);
            if (!response.ok) throw new Error(`Failed to load the history (${response.status})`);
            const data = await response.json();
            // Newest first is the server's ordering; it is not re-sorted here,
            // so the two cannot disagree about what "latest" means.
            setVersions(data.versions || []);
        } catch (err) {
            console.error('Failed to load TME wiki versions:', err);
            if (!silent) {
                setError(err.message);
                setVersions([]);
            }
        }
    }, [wikiId, pageId, expanded]);

    useEffect(() => {
        fetchVersions();
    }, [fetchVersions]);

    // LIV-14: the editor pane bumps this on every tme_wiki_updated - a rollback
    // or a save elsewhere writes a NEW version, so a history list left alone
    // would be missing its newest entry. ⚠️ SILENT: the mount path blanks the
    // list on failure, which is right on mount and wrong for a refresh nobody
    // asked for.
    useEffect(() => {
        if (!refreshKey) return;
        fetchVersions({ silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    // A different page must not show the previous page's history for the
    // instant before the fetch lands.
    useEffect(() => {
        setVersions([]);
        setViewing(null);
    }, [pageId]);

    const viewVersion = async (versionNumber) => {
        try {
            const response = await apiFetch(
                `${API}/wikis/${wikiId}/pages/${pageId}/versions/${versionNumber}`
            );
            const data = await response.json();
            if (!response.ok) {
                window.alert(data.message || 'Could not load that version');
                return;
            }
            setViewing(data);
        } catch (err) {
            console.error('Failed to load a TME wiki version:', err);
            window.alert(err.message);
        }
    };

    const rollBack = async (versionNumber) => {
        if (!window.confirm(rollbackConfirmation(versionNumber))) return;
        setBusy(true);
        try {
            const response = await apiFetch(
                `${API}/wikis/${wikiId}/pages/${pageId}/rollback/${versionNumber}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ version: page.version })
                }
            );
            const data = await response.json();
            if (!response.ok) {
                // A 409 names the conflict - somebody else saved, or the page is
                // archived. Either way the caller reloads rather than retrying
                // against a version number that has moved.
                window.alert(data.message || 'Could not roll the page back');
                if (response.status === 409) await onRolledBack?.();
                return;
            }
            setViewing(null);
            await onRolledBack?.();
            await fetchVersions();
        } finally {
            setBusy(false);
        }
    };

    if (!page) return null;

    return (
        <div className="mt-4 border-t border-gray-200 pt-3">
            <button
                type="button"
                onClick={onToggle}
                className="text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center gap-2"
            >
                <span>{expanded ? '▼' : '►'}</span>
                <FaHistory className="w-3 h-3" />
                History ({page.currentVersion || 0})
            </button>

            {expanded && (
                <div className="mt-2">
                    {error && <p className="text-xs text-red-600">{error}</p>}
                    {!error && versions.length === 0 && (
                        <p className="text-xs text-gray-400 italic pl-4">No saved versions yet</p>
                    )}

                    {versions.map(version => (
                        <div
                            key={version.versionNumber}
                            className="flex items-center justify-between pl-4 py-1 border-b border-gray-100 last:border-b-0"
                        >
                            <span className="min-w-0 flex-1">
                                <span className="text-sm text-gray-800">
                                    v{version.versionNumber} — {version.title}
                                </span>
                                <span className="block text-xs text-gray-500 truncate">
                                    {stamp(version.savedAt)} by {version.savedBy}
                                    {version.versionNotes ? ` — ${version.versionNotes}` : ''}
                                </span>
                            </span>
                            <span className="flex items-center gap-3 flex-shrink-0 pl-2">
                                <button
                                    type="button"
                                    aria-label={`View version ${version.versionNumber}`}
                                    title="View this version"
                                    onClick={() => viewVersion(version.versionNumber)}
                                    className="text-gray-400 hover:text-primary-600"
                                >
                                    <FaEye className="w-3 h-3" />
                                </button>
                                {!readOnly && (
                                    <button
                                        type="button"
                                        aria-label={`Roll back to version ${version.versionNumber}`}
                                        title="Roll back to this version"
                                        disabled={busy}
                                        onClick={() => rollBack(version.versionNumber)}
                                        className="text-gray-400 hover:text-primary-600 disabled:opacity-50"
                                    >
                                        <FaUndo className="w-3 h-3" />
                                    </button>
                                )}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {viewing && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-[70]">
                    <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 p-5 max-h-[80vh] flex flex-col">
                        <div className="flex items-start justify-between mb-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Version {viewing.versionNumber} — {viewing.title}
                            </h3>
                            <button
                                type="button"
                                aria-label="Close version"
                                onClick={() => setViewing(null)}
                                className="text-gray-400 hover:text-gray-700"
                            >
                                <FaTimes className="w-4 h-4" />
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">
                            Saved {stamp(viewing.savedAt)} by {viewing.savedBy}
                            {(viewing.tags || []).length > 0 && ` — tags: ${viewing.tags.join(', ')}`}
                        </p>
                        <div
                            className="flex-1 overflow-y-auto prose prose-sm max-w-none border border-gray-200 rounded p-3"
                            // ⚠️ SANITISED HERE TOO (§7.1), for the same reason the
                            // live page is: what is rendered is purified at the
                            // moment it renders, not at the moment it was stored.
                            dangerouslySetInnerHTML={{ __html: sanitiseWikiHtml(viewing.contentHtml || '') }}
                        />
                        {!readOnly && (
                            <div className="mt-3 flex justify-end">
                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => rollBack(viewing.versionNumber)}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded disabled:opacity-50"
                                >
                                    <FaUndo className="w-3 h-3" />
                                    Roll back to this version
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default VersionHistoryPanel;
